import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import projectsData from "../data/projects.json";
import type { Project, ProjectStatus } from "../types";
import Reveal from "../components/Reveal";
import VideoPlayer from "../components/VideoPlayer";
import { ArrowLeft, ArrowUpRight, Check } from "../components/Icon";

const projects = projectsData.projects as Project[];

const STATUS_LABEL: Record<ProjectStatus, string> = {
  live: "Live",
  archived: "Archived",
  wip: "In progress",
};

export default function ProjectDetail() {
  const { slug } = useParams<{ slug: string }>();
  const project = projects.find((p) => p.slug === slug);

  if (!project) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-8 px-6 text-center">
        <p className="u-label text-muted-foreground">404</p>
        <h1 className="text-h1 font-extrabold uppercase">Project not found</h1>
        <Link to="/#work" className="u-label border border-border px-6 py-3">
          Back to projects
        </Link>
      </div>
    );
  }

  const hasLinks = Boolean(project.externalUrl || project.githubUrl);

  return (
    <article className="pb-32">
      <Helmet>
        <title>{project.title} | Gabriel Nyante</title>
        <meta name="description" content={project.tagline || project.description} />
      </Helmet>

      {/* Hero */}
      <header className="relative h-[70svh] min-h-[420px] w-full overflow-hidden bg-foreground">
        <img
          src={project.image}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-black/55" />

        <div className="relative z-10 mx-auto flex h-full max-w-screen-2xl flex-col justify-end px-6 pb-16">
          <p className="u-label mb-6 text-white/70">
            {STATUS_LABEL[project.status]}
          </p>
          <h1 className="text-display font-extrabold uppercase text-white">
            {project.title}
          </h1>
          {project.tagline && (
            <p className="mt-6 max-w-prose text-lead text-white/75">
              {project.tagline}
            </p>
          )}
        </div>
      </header>

      <div className="mx-auto max-w-screen-2xl px-6">
        <Link
          to="/#work"
          className="group mt-10 inline-flex items-center gap-2 text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
          <span className="u-label">Back to projects</span>
        </Link>

        {/* Overview */}
        <Reveal>
          <section className="mt-20 grid gap-12 border-t border-border pt-16 md:grid-cols-12 md:gap-16">
            <h2 className="u-label text-muted-foreground md:col-span-3">Overview</h2>
            <div className="space-y-5 md:col-span-9">
              {project.fullDescription.split("\n\n").map((para, i) => (
                <p key={i} className="max-w-prose text-lead">
                  {para}
                </p>
              ))}
            </div>
          </section>
        </Reveal>

        {project.features.length > 0 && (
          <Reveal>
            <section className="mt-20 grid gap-12 border-t border-border pt-16 md:grid-cols-12 md:gap-16">
              <h2 className="u-label text-muted-foreground md:col-span-3">Features</h2>
              <ul className="space-y-4 md:col-span-9">
                {project.features.map((feature) => (
                  <li key={feature} className="flex gap-4">
                    <Check className="mt-1 h-5 w-5 shrink-0 text-primary" />
                    <span className="text-lead">{feature}</span>
                  </li>
                ))}
              </ul>
            </section>
          </Reveal>
        )}

        {project.technologies.length > 0 && (
          <Reveal>
            <section className="mt-20 grid gap-12 border-t border-border pt-16 md:grid-cols-12 md:gap-16">
              <h2 className="u-label text-muted-foreground md:col-span-3">Stack</h2>
              <ul className="flex flex-wrap gap-3 md:col-span-9">
                {project.technologies.map((tech) => (
                  <li
                    key={tech}
                    className="u-label border border-border px-4 py-2 text-muted-foreground"
                  >
                    {tech}
                  </li>
                ))}
              </ul>
            </section>
          </Reveal>
        )}

        {project.images.length > 0 && (
          <Reveal>
            <section className="mt-20 border-t border-border pt-16">
              <h2 className="u-label mb-10 text-muted-foreground">Gallery</h2>
              <div className="grid gap-6 md:grid-cols-2">
                {project.images.map((image, i) => (
                  <img
                    key={image}
                    src={image}
                    alt={`${project.title} screenshot ${i + 1}`}
                    loading="lazy"
                    className={`w-full border border-border object-cover ${
                      i % 3 === 0 ? "md:col-span-2" : ""
                    }`}
                  />
                ))}
              </div>
            </section>
          </Reveal>
        )}

        {project.videos.length > 0 && (
          <Reveal>
            <section className="mt-20 grid gap-12 border-t border-border pt-16 md:grid-cols-12 md:gap-16">
              <h2 className="u-label text-muted-foreground md:col-span-3">Video</h2>
              <div className="space-y-14 md:col-span-9">
                {project.videos.map((video) => (
                  <VideoPlayer key={video.url} video={video} />
                ))}
              </div>
            </section>
          </Reveal>
        )}

        {/* curate-feeds has neither link, so this whole row stays hidden. */}
        {hasLinks && (
          <Reveal>
            <section className="mt-20 flex flex-wrap gap-6 border-t border-border pt-16">
              {project.externalUrl && (
                <a
                  href={project.externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-3 border border-foreground px-8 py-4 transition-colors hover:bg-primary hover:border-primary hover:text-primary-foreground"
                >
                  <span className="u-label">View live site</span>
                  <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                </a>
              )}
              {project.githubUrl && (
                <a
                  href={project.githubUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-3 border border-border px-8 py-4 transition-colors hover:border-foreground"
                >
                  <span className="u-label">View source</span>
                  <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                </a>
              )}
            </section>
          </Reveal>
        )}
      </div>
    </article>
  );
}
